import type {
  BookletProfileAttributes,
  BookletPrintProfileId,
  PrintProfileId,
  PrintProfileAttributes,
  ResolvedProfileAttributes,
  ProfileCapabilities,
} from './types';

const PROFILE_DEFINITIONS: Record<PrintProfileId, PrintProfileAttributes> = {
  A4_SIMPLE: {
    id: 'A4_SIMPLE',
    sheetFormat: 'A4',
    media: 'iso_a4_210x297mm',
    sides: 'one-sided',
    finishings: [3],
    printColorMode: 'color',
  },
  A4_BOOKLET: {
    id: 'A4_BOOKLET',
    sheetFormat: 'A4',
    media: 'iso_a4_210x297mm',
    sides: 'two-sided-short-edge',
    finishings: [],
    printColorMode: 'monochrome',
    booklet: {
      finishedPageFormat: 'A5',
      finishingTemplate: 'saddle-stitch',
    },
  },
  A3_BOOKLET: {
    id: 'A3_BOOKLET',
    sheetFormat: 'A3',
    media: 'iso_a3_297x420mm',
    sides: 'two-sided-short-edge',
    finishings: [],
    printColorMode: 'monochrome',
    booklet: {
      finishedPageFormat: 'A4',
      finishingTemplate: 'saddle-stitch',
    },
  },
};

export const PRINT_PROFILE_DEFINITIONS = PROFILE_DEFINITIONS;

export const NOTICE_BOOKLET_PROFILE_ID: BookletPrintProfileId = 'A4_BOOKLET';

export function getPrintProfileDefinition(
  profileId: PrintProfileId,
): PrintProfileAttributes {
  const profile = PROFILE_DEFINITIONS[profileId];
  if (!profile) {
    throw new Error(`Unknown print profile: ${profileId}`);
  }
  return profile;
}

export function getBookletProfileAttributes(
  profileId: PrintProfileId,
): BookletProfileAttributes | undefined {
  return getPrintProfileDefinition(profileId).booklet;
}

const isCapabilityDisabled = (value: boolean | undefined | null): boolean =>
  value === false;

export function resolveProfileAttributes(
  profileId: PrintProfileId,
  capabilities?: ProfileCapabilities,
): ResolvedProfileAttributes {
  const base = getPrintProfileDefinition(profileId);

  const result: ResolvedProfileAttributes = {
    ...base,
    finishings: [...base.finishings],
    booklet: base.booklet ? { ...base.booklet } : undefined,
  };

  if (base.booklet) {
    const hasStapling = !isCapabilityDisabled(capabilities?.supportsStapling);
    const hasFinisher = Boolean(capabilities?.finisher);

    if (!(hasStapling && hasFinisher)) {
      result.degraded = true;
    }

    if (!hasFinisher) {
      result.outputBin = undefined;
    }
  }

  return result;
}
